// src/components/LayerPanel/index.jsx
//
// Tabbed layer catalog. Replaces the old tree-structured LayerPanel.jsx.
//
// Layout is a fixed flex column: sticky PanelHeader → sticky Tabs → the
// scrolling tab content → sticky ActiveFooter. The chrome (header/tabs/footer)
// is compact; only the tab content scrolls.
//
// Each tab owns one scope (Imagery, Assets, AOIs, My Layers) and shows its
// own active state inline — there is no separate Active Layers tray. The
// footer is the single global summary of how many layers are active.

import React, { useState } from "react";
import { Plane, Layers3, Hexagon, User, Map } from "lucide-react";

import BasemapSelector from "./BasemapSelector";
import PanelHeader from "./PanelHeader";
import ActiveLayers from "./ActiveLayers";
import Tabs from "./Tabs";
import ActiveFooter from "./ActiveFooter";
import ImageryTab from "./ImageryTab";
import FeaturesTab from "./FeaturesTab";
import AoisTab from "./AoisTab";
import MyLayersTab from "./MyLayersTab";
import ReferenceTab from "./ReferenceTab";

const TABS = [
  { id: "imagery", label: "Imagery", icon: Plane },
  // Labelled "Assets", not "Features": the tab holds two sub-tabs (RRC and the
  // org's feature catalog) and both halves are asset types at the data layer —
  // the RRC layers carry `assetType`, the catalog is vector_assets. "Features"
  // stays the name of the child that actually is the feature catalog. The tab
  // `id` is deliberately left as "features" so nothing downstream has to move.
  { id: "aois", label: "AOI", icon: Hexagon },
  { id: "features", label: "Assets", icon: Layers3 },
  // "My Layers" is spelled out again: "Features"→"Assets" freed ~14px and
  // tightening the pill padding in Tabs.jsx freed ~30px more, which covers the
  // ~32px it costs over "Mine". "Ref" stays abbreviated — "Reference" needs
  // another ~13px that isn't there. Tab ids are unchanged.
  { id: "mylayers", label: "My Layers", icon: User },
  { id: "reference", label: "Ref", icon: Map },
];

export default function LayerPanel({ userRole = "viewer" }) {
  const [activeTab, setActiveTab] = useState("imagery");

  return (
    <div className="flex flex-col h-full overflow-hidden text-xs select-none">
      <BasemapSelector />
      <PanelHeader />
      <ActiveLayers />
      <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

      <div className="flex-1 overflow-y-auto">
        {activeTab === "imagery" && <ImageryTab />}
        {activeTab === "features" && <FeaturesTab userRole={userRole} />}
        {activeTab === "aois" && <AoisTab />}
        {activeTab === "mylayers" && <MyLayersTab />}
        {activeTab === "reference" && <ReferenceTab />}
      </div>

      <ActiveFooter />
    </div>
  );
}
